import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import "./Registration.css";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import PayButton from "./paypal/PayButton";

export default function Registration() {
  const { path } = useParams();
  // Get the full program ID from the URL
  const programId = path.split("/")[0];
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState([]);

  const location = useLocation();
  const { programPrice, privacyPolicy, termsAndConditions } = location.state || {};

  const totalPrice = programPrice ? Number(programPrice) : (program ? Number(program.programPrice) : 0);
  const courseFee = totalPrice ? (totalPrice / 1.13).toFixed(2) : "0.00";
  const hst = totalPrice ? (totalPrice - totalPrice / 1.13).toFixed(2) : "0.00";

  // Base URL for API calls
  const baseUrl = process.env.REACT_APP_NODE_ENV === 'production'
    ? process.env.REACT_APP_PRODUCTION_BASE_URL
    : process.env.REACT_APP_DEVELOPMENT_BASE_URL;

  const [captcha, setCaptcha] = useState({ svg: "", sessionId: "" });
  const [captchaValue, setCaptchaValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [showFormError, setShowFormError] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('e-transfer');

  // Add state for tracking if registration was successful
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);

  const initialFormState = {
    firstName: "",
    middleName: "",
    lastName: "",
    program: programId,
    programName: "", // Use the full program ID
    gender: "",
    birthDate: "",
    licenseNumber: "",
    licenseExpiryDate: new Date(),
    mobile: "",
    alternatePhone: "",
    email: "",
    parentEmail: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  const notifySuccess = (message) => toast.success(message);
  const notifyError = (error) => toast.error(error);

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormState);
    setCaptchaValue("");
    setErrors({});
    setErrorMessages({});
    setShowFormError(false);
    loadCaptcha();
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/payment-methods`);
        if (response.data.success) {
          setPaymentMethods(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };
    fetchPaymentMethods();
  }, [baseUrl]);

  console.log("paymentMethods", paymentMethods)


  const isPaypalActive = paymentMethods.some(
    (m) => m.methodType?.toLowerCase() === "paypal" && m.status?.toLowerCase() === "active"
  );

  const isInOfficeActive = paymentMethods.some(
    (m) => m.methodType?.toLowerCase() === "in office" && m.status?.toLowerCase() === "active"
  );
  // Fetch program details
  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/course/${programId}`
        );
        if (response.data.success) {
          setProgram(response.data.data);
          console.log("program", response.data.data);
          initialFormState.programName = response.data.data.programName;
        }
      } catch (err) {
        console.error("Error fetching program details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramDetails();
  }, [programId, baseUrl]);

  useEffect(() => {
    loadCaptcha();
  }, []);

  const loadCaptcha = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/user/generate-captcha`
      );
      console.log("Captcha response:", response.data); // Debug log
      if (response.data.success) {
        setCaptcha({
          svg: response.data.data,
          sessionId: response.data.sessionId,
        });
        setCaptchaValue("");
      } else {
        console.error("Failed to load captcha:", response.data);
        notifyError("Failed to load captcha");
      }
    } catch (err) {
      console.error("Error loading captcha:", err);
      notifyError("Failed to load captcha. Please refresh the page.");
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Limit to 10 digits
    const trimmed = cleaned.slice(0, 10);

    // Format as XXX-XXX-XXXX
    if (trimmed.length >= 7) {
      return `${trimmed.slice(0, 3)}-${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
    } else if (trimmed.length >= 4) {
      return `${trimmed.slice(0, 3)}-${trimmed.slice(3)}`;
    } else if (trimmed.length > 0) {
      return trimmed;
    }
    return '';
  };

  const isValidCanadianLicence = (input) => {
    // Remove any dashes or spaces
    const cleaned = input.replace(/[-\s]/g, '');

    // Check max length
    if (cleaned.length > 15) return false;

    // Check if starts with letters (1-3) or is all numeric
    const letterMatch = cleaned.match(/^[A-Z]{1,3}/);
    if (letterMatch) {
      // If it starts with letters, rest must be numbers
      return /^[A-Z]{1,3}\d+$/.test(cleaned);
    } else {
      // If no letters at start, must be all numbers
      return /^\d+$/.test(cleaned);
    }
  };

  const formatLicenseNumber = (value) => {
    let result = '';
    let hasStartedNumbers = false;
    let letterCount = 0;

    // Process each character
    for (let i = 0; i < value.length && result.replace(/[-]/g, '').length < 15; i++) {
      const char = value[i].toUpperCase();

      // Skip dashes and spaces
      if (char === '-' || char === ' ') continue;

      // If we haven't started numbers and have less than 3 letters
      if (!hasStartedNumbers && letterCount < 3 && /[A-Z]/.test(char)) {
        // Add dash after every 5 characters
        if (result.replace(/[-]/g, '').length > 0 && result.replace(/[-]/g, '').length % 5 === 0) {
          result += '-';
        }
        result += char;
        letterCount++;
        continue;
      }

      // If it's a number
      if (/[0-9]/.test(char)) {
        hasStartedNumbers = true;

        // Add dash after every 5 characters
        if (result.replace(/[-]/g, '').length > 0 && result.replace(/[-]/g, '').length % 5 === 0) {
          result += '-';
        }

        result += char;
      }
      // Ignore any letters after we've started numbers or after 3 letters
    }

    return result;
  };

  const handleLicenseChange = (e) => {
    const { value } = e.target;
    const formatted = formatLicenseNumber(value);

    setFormData(prev => ({
      ...prev,
      licenseNumber: formatted
    }));

    // Validate the input
    const cleaned = formatted.replace(/[-\s]/g, '');

    if (value && (
      cleaned.length === 0 ||
      cleaned.length > 15 ||
      !/^[A-Z]{0,3}\d+$/.test(cleaned)
    )) {
      setErrors(prev => ({ ...prev, licenseNumber: true }));
      setErrorMessages(prev => ({
        ...prev,
        licenseNumber: 'Start with up to 3 letters, then numbers only (max 15 characters)'
      }));
    } else {
      setErrors(prev => ({ ...prev, licenseNumber: false }));
      setErrorMessages(prev => ({ ...prev, licenseNumber: '' }));
    }
  };

  // Frontend validation function
  const validateForm = () => {
    const newErrors = {};
    const newErrorMessages = {};
    let hasErrors = false;

    // Required fields validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = true;
      newErrorMessages.firstName = "First name is required";
      hasErrors = true;
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = true;
      newErrorMessages.lastName = "Last name is required";
      hasErrors = true;
    }
    if (!formData.gender) {
      newErrors.gender = true;
      newErrorMessages.gender = "Please select a gender";
      hasErrors = true;
    }
    if (!formData.birthDate) {
      newErrors.birthDate = true;
      newErrorMessages.birthDate = "Birth date is required";
      hasErrors = true;
    }

    // Phone number validation
    if (!formData.mobile.trim()) {
      newErrors.mobile = true;
      newErrorMessages.mobile = "Mobile number is required";
      hasErrors = true;
    } else if (formData.mobile.replace(/\D/g, '').length !== 10) {
      newErrors.mobile = true;
      newErrorMessages.mobile = "Mobile number must be 10 digits";
      hasErrors = true;
    }

    // Alternate phone validation (if provided)
    if (formData.alternatePhone && formData.alternatePhone.replace(/\D/g, '').length !== 10) {
      newErrors.alternatePhone = true;
      newErrorMessages.alternatePhone = "Alternate phone number must be 10 digits";
      hasErrors = true;
    }

    // License number validation (if provided)
    if (formData.licenseNumber && !isValidCanadianLicence(formData.licenseNumber.replace(/-/g, ''))) {
      newErrors.licenseNumber = true;
      newErrorMessages.licenseNumber = "Invalid license number format";
      hasErrors = true;
    }

    if (!formData.email.trim()) {
      newErrors.email = true;
      newErrorMessages.email = "Email is required";
      hasErrors = true;
    }
    if (!formData.address.trim()) {
      newErrors.address = true;
      newErrorMessages.address = "Address is required";
      hasErrors = true;
    }
    if (!formData.city.trim()) {
      newErrors.city = true;
      newErrorMessages.city = "City is required";
      hasErrors = true;
    }
    if (!formData.province) {
      newErrors.province = true;
      newErrorMessages.province = "Province is required";
      hasErrors = true;
    }
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = true;
      newErrorMessages.postalCode = "Postal code is required";
      hasErrors = true;
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = true;
      newErrorMessages.email = "Invalid email format";
      hasErrors = true;
    }

    // Parent email validation (if provided)
    if (
      formData.parentEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parentEmail)
    ) {
      newErrors.parentEmail = true;
      newErrorMessages.parentEmail = "Invalid email format";
      hasErrors = true;
    }

    // Canadian postal code validation
    const postalCodeRegex =
      /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
    if (formData.postalCode && !postalCodeRegex.test(formData.postalCode)) {
      newErrors.postalCode = true;
      newErrorMessages.postalCode =
        "Invalid Canadian postal code format (A1A 1A1)";
      hasErrors = true;
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);

    if (hasErrors) {
      setShowFormError(true);
      setTimeout(() => setShowFormError(false), 5000);
    }

    return !hasErrors;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Use specific handlers for special fields
    if (name === 'licenseNumber') {
      handleLicenseChange(e);
    }
    // Special handling for phone numbers
    else if (name === 'mobile' || name === 'alternatePhone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    }
    // Default handling for other fields
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear errors
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false,
      }));
      setErrorMessages(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle postal code input with masking
  const handlePostalCodeChange = (e) => {
    const { value } = e.target;

    // Convert to uppercase
    let formattedValue = value.toUpperCase();

    // Remove any characters that aren't letters or numbers
    formattedValue = formattedValue.replace(/[^A-Z0-9]/g, '');

    // Apply the Canadian postal code rules
    if (formattedValue.length > 0) {
      // First character must be a letter (except D, F, I, O, Q, U, W, Z)
      if (!/^[ABCEGHJ-NPRSTVXY].*$/.test(formattedValue)) {
        formattedValue = '';
      }
    }
    if (formattedValue.length > 1) {
      // Second character must be a number
      if (!/^[ABCEGHJ-NPRSTVXY][0-9].*$/.test(formattedValue)) {
        formattedValue = formattedValue.charAt(0);
      }
    }
    if (formattedValue.length > 2) {
      // Third character must be a letter (except D, F, I, O, Q, U, W, Z)
      if (!/^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z].*$/.test(formattedValue)) {
        formattedValue = formattedValue.slice(0, 2);
      }
    }
    if (formattedValue.length > 3) {
      // Fourth character must be a number
      if (!/^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][0-9].*$/.test(formattedValue)) {
        formattedValue = formattedValue.slice(0, 3);
      }
    }
    if (formattedValue.length > 4) {
      // Fifth character must be a letter (except D, F, I, O, Q, U, W, Z)
      if (!/^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][0-9][ABCEGHJ-NPRSTV-Z].*$/.test(formattedValue)) {
        formattedValue = formattedValue.slice(0, 4);
      }
    }
    if (formattedValue.length > 5) {
      // Sixth character must be a number
      if (!/^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][0-9][ABCEGHJ-NPRSTV-Z][0-9]$/.test(formattedValue)) {
        formattedValue = formattedValue.slice(0, 5);
      }
    }

    // Limit to 6 characters
    formattedValue = formattedValue.slice(0, 6);

    // Add space after third character
    if (formattedValue.length > 3) {
      formattedValue = formattedValue.slice(0, 3) + ' ' + formattedValue.slice(3);
    }

    setFormData({ ...formData, postalCode: formattedValue });

    // Clear errors
    if (errors.postalCode) {
      setErrors({
        ...errors,
        postalCode: false,
      });
      setErrorMessages({
        ...errorMessages,
        postalCode: "",
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate form before submission
    if (!validateForm()) {
      // Scroll to the first error field
      const errorField = document.querySelector(".error-input");
      if (errorField) {
        errorField.scrollIntoView({ behavior: "smooth", block: "center" });
        errorField.focus();
      }
      return;
    }

    // Validate captcha before showing confirmation modal
    if (!captchaValue.trim()) {
      setErrors((prev) => ({ ...prev, captcha: true }));
      setErrorMessages((prev) => ({
        ...prev,
        captcha: "Please enter the captcha text",
      }));
      notifyError("Please enter the captcha text");

      // Scroll to captcha
      const captchaField = document.querySelector(".captcha-input");
      if (captchaField) {
        captchaField.scrollIntoView({ behavior: "smooth", block: "center" });
        captchaField.focus();
      }
      return;
    }

    if (!captcha.sessionId) {
      notifyError("Captcha session expired. Please refresh the captcha.");
      loadCaptcha();
      return;
    }

    try {
      // Set submitting to prevent multiple submissions
      setIsSubmitting(true);

      // Verify captcha with backend before showing confirmation modal
      // Tell the backend not to consume the captcha so we can use it again for registration
      const captchaVerificationResponse = await axios.post(
        `${baseUrl}/api/user/verify-captcha`,
        {
          captchaSessionId: captcha.sessionId,
          captchaValue: captchaValue,
          consumeCaptcha: false // Don't consume the captcha yet
        }
      );

      if (!captchaVerificationResponse.data.success) {
        notifyError(captchaVerificationResponse.data.error || "Invalid captcha. Please try again.");
        setErrors((prev) => ({ ...prev, captcha: true }));
        setErrorMessages((prev) => ({
          ...prev,
          captcha: "Invalid captcha",
        }));
        loadCaptcha();
        return;
      }

      // Show confirmation modal after successful captcha validation
      setShowConfirmModal(true);
    } catch (err) {
      console.error("Captcha verification error:", err);
      const errorMessage = err.response?.data?.error || "Failed to verify captcha";

      notifyError(errorMessage);
      if (errorMessage.toLowerCase().includes("captcha")) {
        setErrors((prev) => ({ ...prev, captcha: true }));
        setErrorMessages((prev) => ({
          ...prev,
          captcha: "Invalid captcha",
        }));
        loadCaptcha();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle form validation before PayPal payment
  const handleValidateBeforePayment = async () => {
    if (isSubmitting) return false;

    try {
      // The form has already been validated in handleSubmit before showing the modal
      // And we've already validated the captcha there as well
      // So we just need to register the user

      // Set submitting to prevent multiple submissions
      setIsSubmitting(true);

      // If we've already completed registration, don't do it again
      if (registrationCompleted && registeredUserId) {
        setIsSubmitting(false);
        return true;
      }

      // Create a clean copy of the form data
      const requestData = {
        ...formData,
        programName: program.programName,
        program: programId.trim(), // Ensure no whitespace
        captchaSessionId: captcha.sessionId,
        captchaValue: captchaValue,
        paymentMethod: selectedPaymentMethod,
        paymentStatus: "pending" // Mark as pending since we'll complete payment after registration
      };

      console.log("Registering user before payment:", requestData);

      const response = await axios.post(
        `${baseUrl}/api/user/create`,
        requestData
      );

      if (response.data.success) {
        setRegistrationCompleted(true);
        if (response.data.user && response.data.user.id) {
          setRegisteredUserId(response.data.user.id);
        }
        return {
          success: true,
          userId: response.data.user ? response.data.user.id : null
        };
      } else {
        notifyError(
          response.data.error || "Registration failed. Please try again."
        );
        return false;
      }
    } catch (err) {
      console.error("Registration error:", err);
      const errorMessage =
        err.response?.data?.error || "Something went wrong. Please try again.";
      notifyError(errorMessage);

      // Handle specific error types
      if (errorMessage.toLowerCase().includes("captcha")) {
        loadCaptcha();
        setShowConfirmModal(false);
      }

      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modified handleConfirmSubmit for e-transfer method
  const handleConfirmSubmit = async () => {
    if (selectedPaymentMethod === 'e-transfer') {
      // For e-transfer, we complete the registration directly
      try {
        setIsSubmitting(true);

        // Form and captcha already validated in handleSubmit before showing the modal
        // No need to validate again

        // Create a clean copy of the form data
        const requestData = {
          ...formData,
          programName: program.programName,
          program: programId.trim(), // Ensure no whitespace
          captchaSessionId: captcha.sessionId,
          captchaValue: captchaValue,
          paymentMethod: 'e-transfer',
          paymentStatus: 'pending'
        };

        console.log("Sending data to server:", requestData);

        const response = await axios.post(
          `${baseUrl}/api/user/create`,
          requestData
        );

        if (response.data.success) {
          notifySuccess(
            response.data.message || "Registration completed successfully!"
          );
          resetForm();

          // Store user details in session storage for the payment instructions page
          if (response.data.user && response.data.user.id) {
            const paymentDetails = {
              userId: response.data.user.id,
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              programId: programId,
              programName: program.programName,
              amount: program.programPrice,
              paymentMethod: 'e-transfer',
              timestamp: new Date().toISOString()
            };

            window.sessionStorage.setItem('paymentDetails', JSON.stringify(paymentDetails));
            setRegistrationCompleted(true);
            setRegisteredUserId(response.data.user.id);

            // Redirect to payment instructions page with necessary parameters
            setTimeout(() => {
              window.location.href = `/payment-instructions?method=e-transfer&amount=${program.programPrice}&userId=${response.data.user.id}`;
            }, 1500);
          }
        } else {
          notifyError(
            response.data.error || "Registration failed. Please try again."
          );
        }
      } catch (err) {
        // Handle errors
        console.error("Form submission error:", err);
        const errorMessage =
          err.response?.data?.error || "Something went wrong. Please try again.";
        notifyError(errorMessage);

        // Handle specific errors
        if (errorMessage.toLowerCase().includes("captcha")) {
          loadCaptcha();
          setShowConfirmModal(false);
        }
      } finally {
        setIsSubmitting(false);
        setShowConfirmModal(false);
      }
    }
    // For PayPal, the registration will happen when the user clicks the PayPal button
    else if (selectedPaymentMethod === 'paypal') {
      // Validate form before showing PayPal button
      return handleValidateBeforePayment();
    }

    else if (selectedPaymentMethod === 'inOffice') {
      try {
        setIsSubmitting(true);

        const requestData = {
          ...formData,
          programName: program.programName,
          program: programId.trim(),
          captchaSessionId: captcha.sessionId,
          captchaValue: captchaValue,
          paymentMethod: 'inOffice',
          paymentStatus: 'pending'
        };

        const response = await axios.post(`${baseUrl}/api/user/create`, requestData);

        if (response.data.success) {
          notifySuccess("Registration confirmed for in-office payment.");
          resetForm();

          if (response.data.user && response.data.user.id) {
            setRegistrationCompleted(true);
            setRegisteredUserId(response.data.user.id);
          }

          setTimeout(() => {
            window.location.href = `/payment-instructions?method=inOffice&amount=${program.programPrice}&userId=${response.data.user.id}`;
          }, 1500);
        } else {
          notifyError(response.data.error || "Registration failed. Please try again.");
        }
      } catch (err) {
        console.error("In-office registration error:", err);
        notifyError(err.response?.data?.error || "Something went wrong. Please try again.");
      } finally {
        setIsSubmitting(false);
        setShowConfirmModal(false);
      }
    }
  };

  // Add function to cancel submission
  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2000,
          style: {
            background: "#333",
            color: "#fff",
            padding: "16px",
            borderRadius: "8px",
            marginTop: "100px", // Push below navbar
            marginRight: "20px",
            maxWidth: "400px",
            width: "100%"
          },
          success: {
            style: {
              background: "#28a745",
            },
            iconTheme: {
              primary: "#fff",
              secondary: "#28a745",
            },
          },
          error: {
            style: {
              background: "#dc3545",
            },
            iconTheme: {
              primary: "#fff",
              secondary: "#dc3545",
            },
            duration: 3000,
          },
        }}
      />

      {/* Form Error Feedback */}
      {showFormError && (
        <div className="form-feedback">
          <span className="icon">⚠️</span>
          <span>Please correct the highlighted fields</span>
        </div>
      )}

      <section className="who_is_good program_details registration_main">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 mb-3 mb-md-0">
              <div className="position-stick-top">
                <h4>Program Registration – Get Started Today!</h4>
                <div className="mb-3">
                  <p className="w-100">
                    Register now for our comprehensive driver education program
                    and take the first step toward confident and safe driving.
                    Our program includes classroom training, flexible online
                    assignments, in-car lessons, and road test packages tailored
                    to your needs. With experienced instructors, engaging
                    learning materials, and hands-on practice, you'll gain the
                    skills necessary to pass your test and drive with
                    confidence.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="content">
                <div>
                  {/* Tabs Navigation */}
                  <ul className="nav nav-tabs" id="myTab" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="home-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#home"
                        type="button"
                        role="tab"
                        aria-controls="home"
                        aria-selected="true"
                      >
                        Registration Form
                      </button>
                    </li>
                  </ul>

                  {/* Tabs Content */}
                  <div className="tab-content mt-3" id="myTabContent">
                    <div
                      className="tab-pane fade show active"
                      id="home"
                      role="tabpanel"
                      aria-labelledby="home-tab"
                    >
                      <form onSubmit={handleSubmit} className="row">
                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            First Name <span className="asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="Enter First Name"
                            required
                            className={errors.firstName ? "error-input" : ""}
                          />
                          {errors.firstName && (
                            <div className="error-tooltip">
                              {errorMessages.firstName}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>Middle Name</label>
                          <input
                            type="text"
                            name="middleName"
                            value={formData.middleName}
                            onChange={handleChange}
                            placeholder="Enter Middle Name"
                          />
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Last Name <span className="asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Enter Last Name"
                            required
                            className={errors.lastName ? "error-input" : ""}
                          />
                          {errors.lastName && (
                            <div className="error-tooltip">
                              {errorMessages.lastName}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Gender <span className="asterisk">*</span>
                          </label>
                          <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            required
                            className={errors.gender ? "error-input" : ""}
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                          {errors.gender && (
                            <div className="error-tooltip">
                              {errorMessages.gender}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Birth Date <span className="asterisk">*</span>
                          </label>
                          <input
                            type="date"
                            name="birthDate"
                            value={formData.birthDate}
                            onChange={handleChange}
                            required
                            className={errors.birthDate ? "error-input" : ""}
                          />
                          {errors.birthDate && (
                            <div className="error-tooltip">
                              {errorMessages.birthDate}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            License Number
                            <span className="help-tooltip">
                              ℹ️
                              <span className="tooltip-content">
                                Format:<br />
                                • Maximum 15 characters<br />
                                • Can start with up to 3 letters<br />
                                • Rest must be numbers only<br />
                                • Dash after every 5 characters
                              </span>
                            </span>
                          </label>
                          <input
                            type="text"
                            name="licenseNumber"
                            value={formData.licenseNumber}
                            onChange={handleLicenseChange}
                            placeholder="ABC12-34567-890 or 12345-67890"
                            maxLength="17"
                            className={errors.licenseNumber ? "error-input" : ""}
                          />
                          {errors.licenseNumber && (
                            <div className="error-tooltip">
                              {errorMessages.licenseNumber}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>License Expiry Date</label>
                          <input
                            type="date"
                            name="licenseExpiryDate"
                            value={formData.licenseExpiryDate}
                            onChange={handleChange}
                            className={
                              errors.licenseExpiryDate ? "error-input" : ""
                            }
                          />
                          {errors.licenseExpiryDate && (
                            <div className="error-tooltip">
                              {errorMessages.licenseExpiryDate}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Mobile <span className="asterisk">*</span>
                          </label>
                          <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            placeholder="XXX-XXX-XXXX"
                            required
                            maxLength="12"
                            className={errors.mobile ? "error-input" : ""}
                          />
                          {errors.mobile && (
                            <div className="error-tooltip">
                              {errorMessages.mobile}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>Alternate Phone</label>
                          <input
                            type="tel"
                            name="alternatePhone"
                            value={formData.alternatePhone}
                            onChange={handleChange}
                            placeholder="XXX-XXX-XXXX"
                            maxLength="12"
                            className={
                              errors.alternatePhone ? "error-input" : ""
                            }
                          />
                          {errors.alternatePhone && (
                            <div className="error-tooltip">
                              {errorMessages.alternatePhone}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Email <span className="asterisk">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter Email"
                            required
                            className={errors.email ? "error-input" : ""}
                          />
                          {errors.email && (
                            <div className="error-tooltip">
                              {errorMessages.email}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>Parent Email</label>
                          <input
                            type="email"
                            name="parentEmail"
                            value={formData.parentEmail}
                            onChange={handleChange}
                            placeholder="Enter Parent Email"
                            className={errors.parentEmail ? "error-input" : ""}
                          />
                          {errors.parentEmail && (
                            <div className="error-tooltip">
                              {errorMessages.parentEmail}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Address <span className="asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Enter Address"
                            required
                            className={errors.address ? "error-input" : ""}
                          />
                          {errors.address && (
                            <div className="error-tooltip">
                              {errorMessages.address}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            City <span className="asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="Enter City"
                            required
                            className={errors.city ? "error-input" : ""}
                          />
                          {errors.city && (
                            <div className="error-tooltip">
                              {errorMessages.city}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Province <span className="asterisk">*</span>
                          </label>
                          <select
                            name="province"
                            value={formData.province}
                            onChange={handleChange}
                            required
                            className={errors.province ? "error-input" : ""}
                          >
                            <option value="">Select Province</option>
                            <option value="AB">Alberta</option>
                            <option value="BC">British Columbia</option>
                            <option value="MB">Manitoba</option>
                            <option value="NB">New Brunswick</option>
                            <option value="NL">
                              Newfoundland and Labrador
                            </option>
                            <option value="NS">Nova Scotia</option>
                            <option value="NT">Northwest Territories</option>
                            <option value="NU">Nunavut</option>
                            <option value="ON">Ontario</option>
                            <option value="PE">Prince Edward Island</option>
                            <option value="QC">Quebec</option>
                            <option value="SK">Saskatchewan</option>
                            <option value="YT">Yukon</option>
                          </select>
                          {errors.province && (
                            <div className="error-tooltip">
                              {errorMessages.province}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-md-4 col-12 mb-3">
                          <label>
                            Postal Code <span className="asterisk">*</span>
                            <span className="help-tooltip">
                              ℹ️
                              <span className="tooltip-content">
                                Canadian postal code format is A1A 1A1, where A
                                is a letter (except D, F, I, O, Q, U, W, Z) and
                                1 is a digit.
                              </span>
                            </span>
                          </label>
                          <input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handlePostalCodeChange}
                            placeholder="A1A 1A1"
                            required
                            maxLength="7"
                            className={errors.postalCode ? "error-input" : ""}
                          />
                          {errors.postalCode && (
                            <div className="error-tooltip">
                              {errorMessages.postalCode}
                            </div>
                          )}
                        </div>

                        <div className="form-group col-12 mb-4">
                          <label>Program Fees</label>

                          <div className="fee-box">
                            <div className="fee-row">
                              <span>Course Fee:</span>
                              <span>${courseFee}</span>
                            </div>

                            <div className="fee-row">
                              <span>HST (13%):</span>
                              <span>${hst}</span>
                            </div>

                            <div className="fee-row total">
                              <span>Total Price:</span>
                              <span>${totalPrice.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>


                        <div className="form-group col-12 mb-4">
                          <label>
                            Verify Captcha <span className="asterisk">*</span>
                            <small className="text-muted">
                              (Case Sensitive)
                            </small>
                          </label>
                          <div className="captcha-container">
                            {captcha.svg ? (
                              <div
                                className="captcha-image"
                                dangerouslySetInnerHTML={{
                                  __html: captcha.svg,
                                }}
                              />
                            ) : (
                              <div className="captcha-image">
                                Loading captcha...
                              </div>
                            )}
                            <button
                              type="button"
                              className="btn-refresh-captcha"
                              onClick={loadCaptcha}
                              title="Refresh Captcha"
                            >
                              ↻
                            </button>
                          </div>
                          <input
                            type="text"
                            value={captchaValue}
                            onChange={(e) => setCaptchaValue(e.target.value)}
                            placeholder="Enter the characters exactly as shown"
                            required
                            className={`captcha-input ${errors.captcha ? "error-input" : ""
                              }`}
                            maxLength={5}
                          />
                          {errors.captcha && (
                            <div className="error-tooltip">
                              {errorMessages.captcha}
                            </div>
                          )}
                        </div>

                        {/* ✅ Agreement Checkbox */}
                        <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                          <input
                            type="checkbox"
                            id="agreement"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            style={{ width: "18px", height: "18px", cursor: "pointer" }}
                          />
                          <label htmlFor="agreement" style={{ fontSize: "15px", cursor: "pointer", margin: 0 }}>
                            I agree to the{" "}
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                color: "#007bff",
                                textDecoration: "underline",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: "15px",
                              }}
                              onClick={() => setShowTerms(true)}
                              onMouseOver={(e) => (e.target.style.color = "#0056b3")}
                              onMouseOut={(e) => (e.target.style.color = "#007bff")}
                            >
                              Terms & Conditions
                            </button>{" "}
                            and{" "}
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                color: "#007bff",
                                textDecoration: "underline",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: "15px",
                              }}
                              onClick={() => setShowPrivacy(true)}
                              onMouseOver={(e) => (e.target.style.color = "#0056b3")}
                              onMouseOut={(e) => (e.target.style.color = "#007bff")}
                            >
                              Privacy Policy
                            </button>
                          </label>
                        </div>

                        <div className="col-12">
                          <button
                            type="submit"
                            className="btn1 w-100"
                            disabled={isSubmitting || !agreed || location.pathname.includes('dashboard')}
                            style={{
                              opacity: !agreed ? 0.6 : 1,
                              cursor: !agreed ? "not-allowed" : "pointer",
                            }}
                          >
                            {isSubmitting ? "Submitting..." : "Submit"}
                          </button>
                        </div>

                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="confirmation-modal-overlay" style={{
          position: "fixed",
          top: "5%",
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
          padding: "20px"
        }}>
          <div className="confirmation-modal" style={{
            backgroundColor: "white",
            borderRadius: "10px",
            width: "90%",
            maxWidth: "500px",
            height: "80vh", // Increased height
            padding: "24px",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
            overflowY: "auto",
            position: "relative",
            margin: "auto",
            display: "flex",
            flexDirection: "column"
          }}>
            <div className="confirmation-modal-header" style={{
              borderBottom: "1px solid #dee2e6",
              paddingBottom: "16px",
              marginBottom: "20px",
              backgroundColor: "white",
              flex: "none"
            }}>
              <h4 style={{ margin: 0, color: "#2c3e50", fontSize: "20px" }}>
                Registration Confirmation
              </h4>
            </div>
            <div className="confirmation-modal-body" style={{
              flex: 1,
              overflow: "visible"
            }}>
              <p style={{ color: "#495057", marginBottom: "20px" }}>
                Hi {formData.firstName}, please review your registration details:
              </p>

              {program && (
                <div style={{
                  background: "#f8f9fa",
                  borderRadius: "8px",
                  padding: "20px",
                  marginBottom: "20px",
                }}>
                  <div style={{
                    borderBottom: "1px solid #dee2e6",
                    paddingBottom: "15px",
                    marginBottom: "15px",
                  }}>
                    <h5 style={{
                      margin: 0,
                      color: "#2c3e50",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}>{program.programName}</h5>
                    <p style={{
                      margin: "8px 0 0",
                      color: "#6c757d",
                      fontSize: "14px",
                    }}>Program ID: {programId}</p>
                  </div>

                  <div>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "10px",
                      color: "#495057",
                    }}>
                      <span>Program Fee</span>
                      <span style={{
                        fontFamily: "monospace",
                        fontSize: "16px",
                        color: "#2c3e50",
                      }}>${program && Number(program.programPrice).toFixed(2)}</span>
                    </div>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "15px",
                      paddingTop: "15px",
                      borderTop: "2px solid #dee2e6",
                      fontWeight: "bold",
                      color: "#2c3e50",
                    }}>
                      <span>Total Amount</span>
                      <span style={{
                        fontFamily: "monospace",
                        fontSize: "18px",
                        color: "#dc1928",
                      }}>${program && Number(program.programPrice).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div style={{
                marginTop: "20px",
                marginBottom: "20px",
              }}>
                <h5 style={{
                  color: "#2c3e50",
                  fontSize: "16px",
                  marginBottom: "15px",
                }}>Payment Method</h5>

                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}>
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "15px",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: selectedPaymentMethod === 'e-transfer' ? '#f8f9fa' : 'white',
                  }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="e-transfer"
                      checked={selectedPaymentMethod === 'e-transfer'}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      style={{ marginRight: "10px" }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{
                        backgroundColor: "#f0f0f0",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px"
                      }}>
                        💳
                      </span>
                      <span>E-transfer</span>
                    </div>
                  </label>
                  {isPaypalActive && (

                    <label style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "15px",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: selectedPaymentMethod === 'paypal' ? '#f8f9fa' : 'white',
                    }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="paypal"
                        checked={selectedPaymentMethod === 'paypal'}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        style={{ marginRight: "10px" }}
                      />
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{
                          backgroundColor: "#f0f0f0",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px"
                        }}>
                          💳
                        </span>
                        <span>PayPal</span>
                      </div>
                    </label>
                  )}

                  {isInOfficeActive && (

                    <label style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "15px",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: selectedPaymentMethod === 'paypal' ? '#f8f9fa' : 'white',
                    }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="inOffice"
                        checked={selectedPaymentMethod === 'inOffice'}
                        onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                        style={{ marginRight: "10px" }}
                      />
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{
                          backgroundColor: "#f0f0f0",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px"
                        }}>
                          💳
                        </span>
                        <span>in office</span>
                      </div>
                    </label>
                  )}
                </div>

                {selectedPaymentMethod === 'e-transfer' && (
                  <div style={{
                    marginTop: "20px",
                    padding: "15px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}>
                    <p style={{ marginBottom: "10px" }}>
                      After completing your registration, you will be redirected to complete the payment of ${program && Number(program.programPrice).toFixed(2)}.
                    </p>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#6c757d",
                    }}>
                      <span>🔒</span>
                      <span>Secure Connection</span>
                    </div>
                  </div>
                )}

                {selectedPaymentMethod === 'paypal' && (
                  <PayPalScriptProvider
                    options={{
                      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                      "currency": "CAD",
                      "intent": "capture",
                      "components": "buttons",
                      "debug": process.env.REACT_APP_NODE_ENV !== 'production'
                    }}
                  >
                    <div style={{ marginTop: "20px" }}>
                      <PayButton
                        amount={program && Number(program.programPrice)}
                        onClick={handleConfirmSubmit}
                        programInfo={{
                          programId: programId,
                          programName: program?.programName
                        }}
                      />
                    </div>
                  </PayPalScriptProvider>
                )}
              </div>
            </div>
            <div style={{
              borderTop: "1px solid #dee2e6",
              paddingTop: "20px",
              marginTop: "20px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              backgroundColor: "white",
              flex: "none"
            }}>
              <button
                className="btn-cancel"
                onClick={handleCancelSubmit}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all 0.2s",
                }}
              >
                Cancel
              </button>
              {(selectedPaymentMethod === 'e-transfer' || selectedPaymentMethod === 'inOffice') && (
                <button
                  className="btn-confirm"
                  onClick={handleConfirmSubmit}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#dc1928",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "all 0.2s",
                    boxShadow: "0 2px 4px rgba(220, 25, 40, 0.1)",
                  }}
                >
                  Continue
                </button>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ✅ Terms & Conditions Modal */}
      {showTerms && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999999,
          }}
          onClick={() => setShowTerms(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "25px",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              animation: "fadeIn 0.2s ease-in-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ marginBottom: "10px" }}>Terms & Conditions</h5>
            <div
              style={{
                marginBottom: "10px",
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: program.termsAndConditions }}
            />
            <button
              style={{
                marginTop: "15px",
                background: "#007bff",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
              onClick={() => setShowTerms(false)}
              onMouseOver={(e) => (e.target.style.background = "#0056b3")}
              onMouseOut={(e) => (e.target.style.background = "#007bff")}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ✅ Privacy Policy Modal */}
      {showPrivacy && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999999,
          }}
          onClick={() => setShowPrivacy(false)}
        >
          <div
            style={{
              background: "#fff",
              padding: "25px",
              borderRadius: "10px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              animation: "fadeIn 0.2s ease-in-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ marginBottom: "10px" }}>Privacy Policy</h5>
            <div
              style={{
                marginBottom: "10px",
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: program.privacyPolicy }}
            />
            <button
              style={{
                marginTop: "15px",
                background: "#007bff",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
              onClick={() => setShowPrivacy(false)}
              onMouseOver={(e) => (e.target.style.background = "#0056b3")}
              onMouseOut={(e) => (e.target.style.background = "#007bff")}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
